<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        @click.self="close"
      >
        <div
          class="absolute inset-0 bg-black/20 backdrop-blur-md"
          @click="close"
        ></div>
        <div class="relative max-w-sm w-full bg-white rounded-2xl shadow-xl overflow-visible">
          <button
            @click.stop="close"
            class="absolute top-4 end-4 z-50 w-8 h-8 flex items-center justify-center text-black-100 hover:bg-black-05 rounded-full transition-colors"
          >
            <UIIcon
              name="close"
              size="sm"
            />
          </button>

          <div class="flex flex-col items-center pt-14 pb-6 px-6">
            <div class="absolute -top-[35px] left-1/2 transform -translate-x-1/2">
              <img
                src="/crown-icon-with-background.svg"
                :alt="t('learn.exclusiveModal.crownAlt')"
                class="w-[101px] h-[101px]"
              />
            </div>

            <h2 class="text-md text-black-100 font-bold text-center mb-3">
              {{ t('learn.exclusiveModal.title') }}
            </h2>

            <p class="text-[14px] text-black-60 text-center mb-6 leading-relaxed">
              {{ t('learn.exclusiveModal.description') }}
            </p>
            <button
              type="button"
              class="w-full rounded-xl bg-[#FFB648] text-black-100 font-bold text-[16px] leading-[22px] h-[42px] px-4 shadow-sm hover:brightness-95 active:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB648]/50 transition"
              style="font-family: 'Satoshi', sans-serif; letter-spacing: 0px; font-style: bold"
              @click="handleUpgrade"
            >
              {{ t('learn.exclusiveModal.upgradeNow') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { TrainingVideo } from '~/composables/useTraining';

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
  video?: TrainingVideo | null;
}>();

const packageLabel = computed(() => {
  if (!props.video?.packageNames || props.video.packageNames.length === 0) {
    return t('learn.exclusiveModal.premiumFallback');
  }

  if (props.video.packageNames.length === 1) {
    return props.video.packageNames[0];
  }

  const extraCount = props.video.packageNames.length - 1;
  return t(
    'learn.exclusiveModal.packageLabelMore',
    { name: props.video.packageNames[0], count: extraCount },
    extraCount,
  );
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  upgrade: [];
}>();

const { lock, unlock } = useBodyScrollLock();

function close() {
  emit('update:modelValue', false);
}

function handleUpgrade() {
  emit('upgrade');
  close();
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      lock();
    } else {
      unlock();
    }
  },
);

onKeyStroke('Escape', () => {
  if (props.modelValue) {
    close();
  }
});

onBeforeUnmount(() => {
  unlock();
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
  opacity: 0;
}
</style>
